import React, { useCallback, useMemo, useState } from "react"
import { useParams } from "next/navigation"

import { type RerunMetricsById } from "@/metrics/rerun"
import {
  useDeleteSimulationOutputTransport,
  useRunTransportInvalidation,
} from "@/runs"
import { RUN_STATUS } from "@/runs/status"
import { SearchInput, TableColumnToggle, toast } from "@coval/ui"

import { useAnalytics } from "@/lib/analytics"
import { logger } from "@/lib/logger"
import { FilterBarScope } from "@/components/filters/ActiveFilterBar"
import { BrowseTable as Table } from "@/components/tables/BrowseTable"
import {
  TableDisplayScope,
  TableDisplaySlot,
} from "@/components/tables/TableDisplayScope"
import { useInvestigatedSimulationOutputs } from "@/hooks/useInvestigatedSimulationOutputs"
import { useQueryParams } from "@/hooks/useQueryParams"
import type { RunRecord } from "@/types/api/RunRecord"
import type { MetricViews } from "@/types/MetricViewConfig"
import type { RunRowLike } from "@/types/RunRow"

import { type CompareBy } from "../../compare-by"
import { useExplanationSimilarityReorder } from "../hooks/useExplanationSimilarityReorder"
import { useRunResultsBulkDelete } from "../hooks/useRunResultsBulkDelete"
import { useRunResultsColumnDefs } from "../hooks/useRunResultsColumnDefs"
import { useRunResultsFilterActions } from "../hooks/useRunResultsFilterActions"
import {
  useRunResultsFilters,
  type RunResultsFilterController,
} from "../hooks/useRunResultsFilters"
import { useRunResultsMutationCatalog } from "../hooks/useRunResultsMutationCatalog"
import { useRunResultsOutlierRows } from "../hooks/useRunResultsOutlierRows"
import { useRunResultsPropertyFilters } from "../hooks/useRunResultsPropertyFilters"
import { useRunResultsRenderColumns } from "../hooks/useRunResultsRenderColumns"
import { useRunResultsRowNavigation } from "../hooks/useRunResultsRowNavigation"
import { useRunResultsSelection } from "../hooks/useRunResultsSelection"
import { useRunResultsTableColumns } from "../hooks/useRunResultsTableColumns"
import { useRunResultsTestSetGeneration } from "../hooks/useRunResultsTestSetGeneration"
import type {
  RunResultsMetricFilterAdaptersById,
  RunResultsMetricRenderAdaptersById,
  RunResultsMetricSourcesById,
} from "../types"
import {
  getCompareColor as computeCompareColor,
  getCompareDisplayValue as computeCompareDisplayValue,
  getCompareColumnLabel,
} from "../utils/compareColumn"
import { getMetricDisplayLabel as computeMetricDisplayLabel } from "../utils/metricColumnLabel"
import { RunResultsBulkActions } from "./run-results-table/RunResultsBulkActions"
import { RUN_RESULTS_GROUP_OPTIONS } from "./run-results-table/RunResultsDisplayOptions"
import { RunResultsTableActionsScope } from "./run-results-table/RunResultsTableActionsScope"
import { RunResultsTableModals } from "./run-results-table/RunResultsTableModals"
import { RunResultsTableSkeleton } from "./run-results-table/RunResultsTableSkeleton"
import { useRunResultRowHrefs } from "./run-results-table/useRunResultRowHrefs"
import { RunFilterDropdown } from "./RunFilterDropdown"
import { RunResultsItemCount } from "./RunResultsItemCount"
import { SimilarityThresholdSlider } from "./SimilarityThresholdSlider"

const log = logger("RunResultsTable")
type RunResultsTableRunRecord = Pick<RunRecord, "agent_id"> | null | undefined

interface RunResultsTableProps {
  metricIdAliases?: Readonly<Record<string, string>>
  filters?: RunResultsFilterController
  metricFilterAdaptersById: RunResultsMetricFilterAdaptersById
  metricRenderAdaptersById: RunResultsMetricRenderAdaptersById
  /** Richer per-metric source for type-aware filter value pre-fill (categorical options + metadata). */
  metricSourcesById: RunResultsMetricSourcesById
  runRows: RunRowLike[]
  outlierSourceRows?: RunRowLike[]
  runRecord: RunResultsTableRunRecord
  onRowClick: (simulationOutputId: string) => void
  /** When set, live rows show a listen affordance that opens the live-sims panel. */
  onListenLive?: (simulationOutputId: string) => void
  onSelectionChange?: (selectedIds: string[]) => void
  isLoading?: boolean
  /** When set, only show this metric's column (e.g. when viewing in-depth run review for that metric). */
  metricIdFilter?: string | null
  /** Multi-run: when set, show compare column; table reads values from RunRow. */
  compareBy?: CompareBy | null
  /** Multi-run: optional display name lookups for compare-by agent/persona. */
  agentsById?: Record<string, { display_name: string }> | null
  personasById?: Record<string, { display_name: string }> | null
  /** When true, disables auth-gated tRPC queries and write actions (for public shared pages). */
  isShared?: boolean
  /** Per-metric visibility config. When provided, hides metric columns where visible === false. */
  metricViews?: MetricViews
  onRowHover?: (simulationOutputId: string | null) => void
  /** Catalog used by the Rerun bulk-action button. When omitted, the bulk-action bar omits rerun. */
  rerunMetricRecordsById?: RerunMetricsById | null
  navigationScopeStorageKey?: string
  /** This run's tag control. It sits with the table's own controls, which is where it reads as belonging. */
  tableTagAction?: React.ReactNode
  /** Actions on this run — resimulate, rerun, review — rendered in the controls row rather than the page header. */
  tableRunActions?: React.ReactNode
  /** True when the supplied run actions own bulk deletion; otherwise the table provides it. */
  tableActionsHostSelectionDelete?: boolean
  /** The results table's own scroll container. The card around this component clips rather than scrolls, so scroll-position save/restore reads this. */
  scrollRef?: React.Ref<HTMLDivElement | null>
}

export const RunResultsTable: React.FC<RunResultsTableProps> = ({
  filters: controlledFilters,
  metricFilterAdaptersById,
  metricRenderAdaptersById,
  metricSourcesById,
  metricIdAliases,
  outlierSourceRows,
  runRows,
  runRecord,
  onRowClick,
  onListenLive,
  onSelectionChange,
  isLoading = false,
  metricIdFilter = null,
  compareBy = null,
  agentsById = null,
  personasById = null,
  isShared = false,
  metricViews,
  onRowHover,
  rerunMetricRecordsById = null,
  navigationScopeStorageKey,
  tableTagAction,
  tableRunActions,
  tableActionsHostSelectionDelete = false,
  scrollRef,
}) => {
  const internalFilters = useRunResultsFilters({
    metricIdAliases,
    metricRecordsById: metricFilterAdaptersById,
    runRows,
  })
  const filters = controlledFilters ?? internalFilters
  const {
    filteredRunRows: baseFilteredRunRows,
    metricFilters,
    mutationFilterIds,
    isOutliersEnabled,
    outlierMetricIds,
    statusFilterStatuses,
    addMetricFilters,
    clearAllFilters,
    clearMetricFilters,
    setMetricFilters,
    setMutationFilterIds,
    setOutliersEnabled,
    setOutlierMetricIds,
    setStatusFilterStatuses,
  } = filters
  const propertyFilters = useRunResultsPropertyFilters(
    baseFilteredRunRows,
    runRows,
    agentsById,
    personasById,
    isShared,
  )
  const filteredRunRows = propertyFilters.rows
  const compareColumnLabel = getCompareColumnLabel(compareBy)

  const getCompareDisplayValue = useCallback(
    (row: RunRowLike): string =>
      computeCompareDisplayValue(row, compareBy, agentsById, personasById),
    [compareBy, agentsById, personasById],
  )

  const getCompareColor = useCallback(
    (row: RunRowLike): string | undefined =>
      computeCompareColor(row, compareBy),
    [compareBy],
  )

  const { trackEvent } = useAnalytics()
  const params = useParams()
  const runId =
    typeof params.run_id === "string" && params.run_id.length > 0
      ? params.run_id
      : undefined
  const queryParams = useQueryParams()
  const { invalidateRunDetail, invalidateRunsLists } =
    useRunTransportInvalidation()

  const { markAsInvestigated, isInvestigated } =
    useInvestigatedSimulationOutputs(params.run_id as string | undefined)

  const agentId = runRecord?.agent_id
  const { mutationNameById, mutationById } = useRunResultsMutationCatalog({
    agentId,
    isShared,
    runRows,
  })

  const [isCompactColumns] = useState(true)

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const onOpenDeleteModal = useCallback(() => setIsDeleteModalOpen(true), [])
  const onCloseDeleteModal = useCallback(() => setIsDeleteModalOpen(false), [])

  const [itemsToDelete, setItemsToDelete] = useState<string[]>([])
  const [filterOpen, setFilterOpen] = useState(false)
  const [filterSection, setFilterSection] = useState<string | null>(null)

  const deleteSimulationOutput = useDeleteSimulationOutputTransport({
    onSuccess: () => {
      toast.info({
        title: "Success",
        description: "Run result deleted successfully",
        duration: 6000,
      })
      const invalidations: Promise<unknown>[] = [invalidateRunsLists()]
      if (typeof params.run_id === "string") {
        invalidations.push(invalidateRunDetail(params.run_id))
      }
      void Promise.all(invalidations)
    },
    onError: (error: { message?: string }) => {
      toast.error({
        title: "Error",
        error,
        fallback: "Failed to delete simulation output.",
        duration: 6000,
      })
    },
  })

  const getMetricDisplayLabel = useCallback(
    (metricKey: string, isCompact: boolean) =>
      computeMetricDisplayLabel(metricKey, isCompact, metricRenderAdaptersById),
    [metricRenderAdaptersById],
  )

  const hasAnyMutations = useMemo(
    () =>
      runRows.some(
        (row) => row.mutation_id != null || row.mutation_name != null,
      ),
    [runRows],
  )
  const showMutationColumn = hasAnyMutations

  const explanationTable = useMemo(
    () =>
      metricIdFilter != null &&
      metricIdFilter !== "" &&
      metricRenderAdaptersById?.[metricIdFilter]
        ? metricRenderAdaptersById[metricIdFilter].getExplanationTable(runRows)
        : { columns: [], getRowValues: () => ({}) as Record<string, string> },
    [metricIdFilter, metricRenderAdaptersById, runRows],
  )

  const explanationSimilarity = useExplanationSimilarityReorder({
    metricIdFilter,
    metricRenderAdaptersById,
    runRows,
  })

  const allColumns = useRunResultsColumnDefs({
    compareColumnLabel,
    explanationTable,
    getMetricDisplayLabel,
    isCompactColumns,
    metricIdFilter,
    metricRenderAdaptersById,
    metricViews,
    runRows,
    showMutationColumn,
  })

  const {
    effectiveVisibleColumns,
    effectiveColumnOrder,
    visibleOrderedColumns,
    columnToggleDefs,
    columnVisibility,
    handleColumnVisibilityChange,
    handleResetColumns,
    setColumnOrder,
  } = useRunResultsTableColumns({
    allColumns,
    metricRenderAdaptersById,
    isShared,
    metricOrderIsControlled: metricViews != null,
  })

  const {
    getOutlierCellVariant,
    isOutlierAnalysisPending,
    outlierEvidenceBySimulationId,
    outlierSimulationOutputIds,
    outliersEnabled,
    outliersQuery,
    scopedRowDisplayIndex,
    scopedRunRows,
    selectedOutliers,
  } = useRunResultsOutlierRows({
    sourceRunRows: outlierSourceRows,
    metricIdAliases,
    filteredRunRows,
    runRows,
    isOutliersEnabled,
    isShared,
    metricIdFilter,
    metricRenderAdaptersById,
    outlierMetricIds,
    reorderRows: explanationSimilarity.reorder,
    runId,
  })

  const {
    setSelectedKeys,
    selectableSimulationOutputIds,
    selectedSimulationOutputIds,
    selectedTestCaseIds,
    selectedCount,
  } = useRunResultsSelection({
    scopedRunRows,
    onSelectionChange,
  })

  const cancelledSelectionDisabledReason = useMemo(() => {
    if (selectedSimulationOutputIds.length === 0) {
      return undefined
    }
    const selectedIdSet = new Set(selectedSimulationOutputIds)
    const hasCancelledSelection = scopedRunRows.some(
      (row) =>
        selectedIdSet.has(row.simulation_output_id) &&
        row.error_status === RUN_STATUS.CANCELLED,
    )
    return hasCancelledSelection
      ? "One or more selected simulations were cancelled."
      : undefined
  }, [scopedRunRows, selectedSimulationOutputIds])

  const rerunMetricsCount =
    metricIdFilter != null && metricIdFilter !== ""
      ? 1
      : Object.keys(rerunMetricRecordsById ?? {}).length
  const rerunMetricsLabel = `Rerun ${rerunMetricsCount} Metric${
    rerunMetricsCount === 1 ? "" : "s"
  }`
  const handleRerunSuccess = useCallback(() => {
    const invalidations: Promise<unknown>[] = [invalidateRunsLists()]
    if (runId) {
      invalidations.push(invalidateRunDetail(runId))
    }
    void Promise.all(invalidations)
  }, [invalidateRunDetail, invalidateRunsLists, runId])

  const { rowCopyId, rowHref } = useRunResultRowHrefs({
    queryParams,
    runId: String(params.run_id),
    slug: params.slug as string,
  })

  const {
    handlePointerEnter,
    handlePointerLeave,
    handleRowClick,
    handleRowOpenInNewTab,
    visibleSimulationOutputId,
  } = useRunResultsRowNavigation({
    markAsInvestigated,
    navigationScopeStorageKey,
    onRowClick,
    onRowHover,
    runId: typeof params.run_id === "string" ? params.run_id : undefined,
    scopedRunRows,
    trackEvent,
  })

  const tableColumns = useRunResultsRenderColumns({
    agentsById,
    compareColumnLabel,
    explanationTable,
    getCompareColor,
    getCompareDisplayValue,
    metricIdFilter,
    metricRenderAdaptersById,
    mutationById,
    mutationNameById,
    onListenLive,
    outlierEvidenceBySimulationId,
    runRecord,
    scopedRowDisplayIndex,
    scopedRunRows,
    visibleOrderedColumns,
  })

  const getTestSetHref = useCallback(
    (testSetId: string) =>
      queryParams.getHref(`/test-sets/${testSetId}`, params.slug as string),
    [params.slug, queryParams],
  )
  const {
    close: closeGenerateTestSetModal,
    confirm: confirmGenerateTestSet,
    isOpen: isGenerateTestSetModalOpen,
    isPending: isGeneratingTestSet,
    open: openGenerateTestSetModal,
  } = useRunResultsTestSetGeneration({
    getTestSetHref,
    scopedRunRows,
    selectedSimulationOutputIds,
  })

  const {
    handleAddMetricFilter,
    handleClearAllFilters,
    handleClearAllMetricFilters,
    handleRemoveMetricFilter,
  } = useRunResultsFilterActions({
    addMetricFilters,
    clearAllFilters: () => {
      clearAllFilters()
      propertyFilters.controls.reset()
    },
    clearMetricFilters,
    metricFilters,
    mutationFilterIds,
    setMetricFilters,
    setMutationFilterIds,
    setStatusFilterStatuses,
    statusFilterStatuses,
  })

  const { confirmBulkDelete, handleBulkDelete } = useRunResultsBulkDelete({
    deleteSimulationOutput,
    itemsToDelete,
    log,
    onCloseDeleteModal,
    onOpenDeleteModal,
    selectedSimulationOutputIds,
    setItemsToDelete,
    setSelectedKeys,
  })

  const showControlsRow = !isLoading || !!tableRunActions || !!tableTagAction

  const showToolbarCsvExport =
    !tableRunActions && !isLoading && selectedCount === 0

  return (
    <TableDisplayScope
      search={propertyFilters.searchQuery}
      filterControls={propertyFilters.controls}
      filters={{
        Status: statusFilterStatuses,
        Mutations: mutationFilterIds,
        Metrics: metricFilters,
        Outliers: outliersEnabled ? "Only outliers" : undefined,
      }}
      className="w-full h-full flex flex-col"
    >
      {showControlsRow && (
        <FilterBarScope className="table-pad flex-none border-b border-coval-border bg-surface-default">
          <div className="flex items-start gap-2 justify-between">
            <div className="flex flex-wrap items-center gap-2 flex-1">
              {isLoading ? null : selectedCount === 0 ? (
                <>
                  <SearchInput
                    aria-label="Search simulations"
                    placeholder="Search simulations…"
                    value={propertyFilters.searchQuery}
                    onChange={(event) =>
                      propertyFilters.setSearchQuery(event.target.value)
                    }
                    onClear={() => propertyFilters.setSearchQuery("")}
                    className="w-64"
                  />
                  <RunFilterDropdown
                    onClearAllFilters={handleClearAllFilters}
                    propertyFilters={propertyFilters.controls}
                    isOpen={filterOpen}
                    onOpenChange={setFilterOpen}
                    section={filterSection}
                    onSectionChange={setFilterSection}
                    mutationNameById={mutationNameById}
                    mutationFilterIds={mutationFilterIds}
                    onMutationFilterChange={setMutationFilterIds}
                    statusFilterStatuses={statusFilterStatuses}
                    onStatusFilterChange={setStatusFilterStatuses}
                    isOutliersEnabled={outliersEnabled}
                    onOutliersEnabledChange={
                      !isShared && runId ? setOutliersEnabled : undefined
                    }
                    outlierMetricIds={selectedOutliers.selectedMetricIds}
                    outlierMetricOptions={
                      selectedOutliers.availableMetricOptions
                    }
                    onOutlierMetricIdsChange={
                      !isShared && runId ? setOutlierMetricIds : undefined
                    }
                    metricRecordsById={metricRenderAdaptersById}
                    metricSourcesById={metricSourcesById}
                    runRows={runRows}
                    metricFilters={metricFilters}
                    onAddMetricFilter={handleAddMetricFilter}
                    onRemoveMetricFilter={handleRemoveMetricFilter}
                    onClearAllMetricFilters={handleClearAllMetricFilters}
                  />

                  <TableDisplaySlot />
                </>
              ) : (
                <RunResultsBulkActions
                  cancelledSelectionDisabledReason={
                    cancelledSelectionDisabledReason
                  }
                  isGeneratingTestSet={isGeneratingTestSet}
                  isShared={isShared}
                  metricIdFilter={metricIdFilter}
                  onBulkDelete={handleBulkDelete}
                  onGenerateTestSet={openGenerateTestSetModal}
                  onRerunSuccess={handleRerunSuccess}
                  rerunMetricRecordsById={rerunMetricRecordsById}
                  rerunMetricsCount={rerunMetricsCount}
                  rerunMetricsLabel={rerunMetricsLabel}
                  runId={runId}
                  selectableSimulationOutputIds={selectableSimulationOutputIds}
                  selectedSimulationOutputIds={selectedSimulationOutputIds}
                  selectedTestCaseIds={selectedTestCaseIds}
                  showDeleteAction={!tableActionsHostSelectionDelete}
                />
              )}
              {tableTagAction}
              <RunResultsTableActionsScope
                allColumns={allColumns}
                columnOrder={effectiveColumnOrder}
                compareColumnLabel={compareColumnLabel}
                explanationTable={explanationTable}
                getCompareDisplayValue={getCompareDisplayValue}
                isDeletingSelected={deleteSimulationOutput.isPending}
                metricRenderAdaptersById={metricRenderAdaptersById}
                mutationNameById={mutationNameById}
                onDeleteSelected={handleBulkDelete}
                rows={scopedRunRows}
                selectedCount={selectedCount}
                showToolbarExport={showToolbarCsvExport}
                visibleColumns={effectiveVisibleColumns}
              >
                {tableRunActions}
              </RunResultsTableActionsScope>
              {isLoading || selectedCount > 0 ? null : (
                <div className="ml-auto">
                  <RunResultsItemCount
                    hasExplicitNoSelection={
                      selectedOutliers.hasExplicitNoSelection
                    }
                    hasLowSampleWarning={selectedOutliers.hasLowSampleWarning}
                    hasNoUsableMetricData={
                      selectedOutliers.hasNoUsableMetricData
                    }
                    hasOutlierError={outliersQuery.isError}
                    isOutlierAnalysisPending={isOutlierAnalysisPending}
                    itemCount={scopedRunRows.length}
                    outlierCount={outlierSimulationOutputIds.size}
                    outliersEnabled={outliersEnabled}
                  />
                </div>
              )}
            </div>

            {!isLoading &&
            selectedCount === 0 &&
            explanationSimilarity.isActive ? (
              <SimilarityThresholdSlider
                value={explanationSimilarity.similarityThreshold}
                onChange={explanationSimilarity.setSimilarityThreshold}
                className="px-2"
              />
            ) : null}
          </div>
        </FilterBarScope>
      )}

      {isLoading ? (
        <div className="flex-1 min-h-0 min-w-0 bg-surface-default">
          <RunResultsTableSkeleton rowCount={5} columnCount={6} />
        </div>
      ) : (
        <div
          data-testid="run-results-table"
          className="flex-1 min-h-0 min-w-0 bg-surface-default"
          data-covi-view-list="simulation"
          data-covi-list-total={scopedRunRows.length}
        >
          <Table
            columnControls={
              <TableColumnToggle
                inline
                label="Visible columns"
                columns={columnToggleDefs}
                columnVisibility={columnVisibility}
                onColumnVisibilityChange={handleColumnVisibilityChange}
                columnOrder={effectiveColumnOrder}
                onColumnOrderChange={setColumnOrder}
                onReset={handleResetColumns}
                searchable
              />
            }
            groupOptions={RUN_RESULTS_GROUP_OPTIONS}
            scrollRef={scrollRef}
            aria-label="Run results table"
            data={scopedRunRows}
            cellVariant={getOutlierCellVariant}
            columns={tableColumns}
            columnOrder={effectiveColumnOrder}
            onColumnOrderChange={setColumnOrder}
            measureRow={() =>
              effectiveVisibleColumns.has("testCaseDescription")
            }
            measuredRowsKey={String(
              effectiveVisibleColumns.has("testCaseDescription"),
            )}
            csvControl={false}
            getRowId={(row) =>
              row.simulation_output_id ||
              `sim-${scopedRowDisplayIndex.get(row) ?? 0}`
            }
            rowCopyId={rowCopyId}
            rowHref={rowHref}
            onRowClick={(row) => handleRowClick(row.simulation_output_id ?? "")}
            onRowOpenInNewTab={handleRowOpenInNewTab}
            activeRowId={visibleSimulationOutputId}
            onRowPointerEnter={handlePointerEnter}
            onRowPointerLeave={handlePointerLeave}
            rowAttributes={(row) =>
              row.simulation_output_id
                ? {
                    "data-testid": `run-result-row-${row.simulation_output_id}`,
                    "data-simulation-output-id": row.simulation_output_id,
                    // Simulation anchor for Sofia view context.
                    "data-covi-highlight-id": row.simulation_output_id,
                    "data-covi-status": row.error_status ?? undefined,
                  }
                : {}
            }
            isRowUnread={(row) =>
              row.simulation_output_id
                ? !isInvestigated(row.simulation_output_id)
                : false
            }
            emptyState="No results found"
            className="h-full"
            selectable
            selectedIds={new Set(selectedSimulationOutputIds)}
            onSelectionChange={(ids: Set<string>) =>
              setSelectedKeys(new Set(ids))
            }
            isRowSelectable={(row: RunRowLike) =>
              typeof row.simulation_output_id === "string" &&
              row.simulation_output_id.length > 0
            }
          />
        </div>
      )}

      <RunResultsTableModals
        deleteCount={itemsToDelete.length}
        isDeleteModalOpen={isDeleteModalOpen}
        isDeletePending={deleteSimulationOutput.isPending}
        isGenerateTestSetModalOpen={isGenerateTestSetModalOpen}
        isGeneratingTestSet={isGeneratingTestSet}
        onCloseDeleteModal={onCloseDeleteModal}
        onCloseGenerateTestSet={closeGenerateTestSetModal}
        onConfirmBulkDelete={() => {
          void confirmBulkDelete()
        }}
        onConfirmGenerateTestSet={() => {
          void confirmGenerateTestSet()
        }}
        selectedCount={selectedCount}
      />
    </TableDisplayScope>
  )
}
